import type {
  MovieFilters,
  MovieSortOptions,
  SortDirection,
  SortOption,
} from "@/types/movie";

/**
 * The browse page keeps its search, filters and sort in the URL so results
 * can be shared and survive reloads/back navigation. These helpers convert
 * between query strings and state. Pure; safe on the server and the client.
 */

export interface BrowseState {
  filters: MovieFilters;
  sort: MovieSortOptions;
}

export const SORT_OPTIONS: readonly SortOption[] = [
  "imdb",
  "rotten_tomatoes_critic",
  "rotten_tomatoes_audience",
  "tmdb",
  "cage_mdb",
  "release_date",
  "title",
  "runtime",
  "box_office",
  "awards",
  "popularity",
];

export const DEFAULT_SORT: MovieSortOptions = {
  sortBy: "imdb",
  direction: "desc",
};

/** Title sorts A-Z by default; everything else high-to-low. */
export function getDefaultDirection(sortBy: SortOption): SortDirection {
  return sortBy === "title" ? "asc" : "desc";
}

/** Numeric filters and their query parameter names. */
const NUMBER_PARAMS = [
  ["yearFrom", "yearFrom"],
  ["yearTo", "yearTo"],
  ["ratingMin", "ratingMin"],
  ["ratingMax", "ratingMax"],
  ["imdbMin", "imdbMin"],
  ["imdbMax", "imdbMax"],
  ["rottenTomatoesCriticMin", "rtCriticMin"],
  ["rottenTomatoesCriticMax", "rtCriticMax"],
  ["rottenTomatoesAudienceMin", "rtAudienceMin"],
  ["rottenTomatoesAudienceMax", "rtAudienceMax"],
  ["metacriticMin", "metacriticMin"],
  ["metacriticMax", "metacriticMax"],
  ["cageMdbMin", "cageMdbMin"],
  ["cageMdbMax", "cageMdbMax"],
] as const satisfies ReadonlyArray<readonly [keyof MovieFilters, string]>;

type NumberFilterKey = (typeof NUMBER_PARAMS)[number][0];

const MAX_SEARCH_LENGTH = 100;

function parseNumber(value: string | null): number | undefined {
  if (value === null || value.trim() === "") return undefined;
  const number = Number(value);

  return Number.isFinite(number) ? number : undefined;
}

type QueryLike = Pick<URLSearchParams, "get">;

export function parseBrowseParams(params: QueryLike): BrowseState {
  const filters: MovieFilters = {};
  const search = params.get("search")?.trim().slice(0, MAX_SEARCH_LENGTH);

  if (search) filters.search = search;

  const genres = (params.get("genres") ?? "")
    .split(",")
    .map((genre) => genre.trim())
    .filter(Boolean);

  if (genres.length > 0) filters.genres = [...new Set(genres)];

  for (const [key, param] of NUMBER_PARAMS) {
    const value = parseNumber(params.get(param));

    if (value !== undefined) filters[key as NumberFilterKey] = value;
  }

  const sortParam = params.get("sortBy");
  const sortBy = SORT_OPTIONS.includes(sortParam as SortOption)
    ? (sortParam as SortOption)
    : DEFAULT_SORT.sortBy;
  const directionParam = params.get("direction");
  const direction: SortDirection =
    directionParam === "asc" || directionParam === "desc"
      ? directionParam
      : sortParam
        ? getDefaultDirection(sortBy)
        : DEFAULT_SORT.direction;

  return { filters, sort: { sortBy, direction } };
}

/** Query string for a state; defaults are omitted to keep URLs short. */
export function serializeBrowseParams(state: BrowseState): string {
  const params = new URLSearchParams();
  const { filters, sort } = state;

  if (filters.search?.trim()) params.set("search", filters.search.trim());
  if (filters.genres && filters.genres.length > 0) {
    params.set("genres", filters.genres.join(","));
  }
  for (const [key, param] of NUMBER_PARAMS) {
    const value = filters[key];

    if (value !== undefined && Number.isFinite(value)) {
      params.set(param, String(value));
    }
  }
  if (
    sort.sortBy !== DEFAULT_SORT.sortBy ||
    sort.direction !== DEFAULT_SORT.direction
  ) {
    params.set("sortBy", sort.sortBy);
    params.set("direction", sort.direction);
  }

  return params.toString();
}

/** Filter groups, counted once each for the "Filters (n)" badge. */
const FILTER_GROUPS: ReadonlyArray<ReadonlyArray<keyof MovieFilters>> = [
  ["yearFrom", "yearTo"],
  ["ratingMin", "ratingMax"],
  ["imdbMin", "imdbMax"],
  ["rottenTomatoesCriticMin", "rottenTomatoesCriticMax"],
  ["rottenTomatoesAudienceMin", "rottenTomatoesAudienceMax"],
  ["metacriticMin", "metacriticMax"],
  ["cageMdbMin", "cageMdbMax"],
];

/** Number of active filters (genres count once; a min/max pair counts once). */
export function countActiveFilters(filters: MovieFilters): number {
  return (
    ((filters.genres?.length ?? 0) > 0 ? 1 : 0) +
    FILTER_GROUPS.filter((group) =>
      group.some((key) => filters[key] !== undefined),
    ).length
  );
}

/** Whether any filter other than the search query is active. */
export function hasActiveFilters(filters: MovieFilters): boolean {
  return countActiveFilters(filters) > 0;
}

/** Clear every filter but keep the search query. */
export function clearFilters(filters: MovieFilters): MovieFilters {
  return filters.search ? { search: filters.search } : {};
}
